import {
  FC,
  ReactElement,
  useEffect,
  useState
} from 'react';
import {
  AudioType,
  defaultTtsVoice,
  DesignLanguage,
  DesignModule,
  DesignModuleNode,
  EDITOR_ROLES,
  TextToSpeechVoiceOrDefault
} from '../../types';
import { useDesign, useDesignRoles, useServices } from '../../hooks';
import { AudioController } from '../audio-controller';
import { getLanguageLabel, getPromptText } from '../../utilities';
import { Button } from '@platform/ui-library';
import styles from './style/PromptTextAreas.module.scss';

export type PromptAudioControllerProps = {
  disabled?: boolean;
  language: DesignLanguage;
  node: DesignModuleNode;
  nodeModule: DesignModule;
  tooltip?: ReactElement | string;
  ttsVoice?: TextToSpeechVoiceOrDefault;
};

const loadingMessage = 'Loading...';

export const PromptAudioController: FC<PromptAudioControllerProps> = ({
  disabled = false,
  language,
  node,
  nodeModule,
  tooltip,
  ttsVoice
}) => {
  const serialized = useDesign();
  const { textToPromptService } = useServices();
  const [message, setMessage] = useState<ReactElement | string | null>('');
  const [url, setUrl] = useState<string | null>(null);
  const { hasDesignRole } = useDesignRoles();

  const generateAudio = async (doFetch = true) => {
    if (message === loadingMessage || ttsVoice === defaultTtsVoice) {
      return;
    }
    setMessage(loadingMessage);
    setUrl(null);
    const [audioUrl, audioType] = await textToPromptService.getAudioAndType({
      doFetch,
      language,
      node,
      nodeModule,
      serialized
    });
    setUrl(audioUrl);
    if (audioUrl) {
      setMessage(<>{
        getLanguageLabel({
          designLanguage: language,
          includeTts: audioType === AudioType.tts,
          includeVt: audioType === AudioType.ttp
        })
      }</>);
    } else {
      setMessage(null);
    }
  };

  const text = getPromptText({ languageCode: language.language, node, ttsVoice });
  useEffect(() => {
    setUrl(null);
    setMessage(null);
    generateAudio(false);
  }, [text, ttsVoice]);

  const removeFailedAudio = () => {
    textToPromptService.removeAudioFromLocalStorage(language, node);
    setUrl(null);
    setMessage('There was a problem trying to load the audio file. Please try generating it again.');
  };

  disabled = !!url || disabled;
  const isDesigner = hasDesignRole(EDITOR_ROLES);

  return (
    <div className={styles.audioControllerWrapper}>
      <div className={styles.audioController}>
        <div className={styles.message}>{message || tooltip}</div>
        {(!isDesigner) ? null : (
          <Button
            disabled={disabled}
            onClick={() => generateAudio()}
            style={{ flexShrink: '0' }}
            theme={disabled ? 'outline' : 'outlineHighlight'}
            type="button"
          >
            ☊ Generate audio
          </Button>
        )}
      </div>
      {!!url && (
        <AudioController
          url={url}
          onError={removeFailedAudio}
        />
      )}
    </div>
  );
};
